import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AppService from '../../../services/AppService';
import { API_BASE_URL } from '../../../utils/constants';
import workflowService from '../../../services/workflow';
import { useDispatch } from 'react-redux';
import { showLoader, dismissLoader } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  InputAdornment,
  Divider,
  Checkbox,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Autocomplete from '@mui/material/Autocomplete';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getEffectiveToken } from '../../../utils/tokenUtils';

// Helper functions
const formatCurrency = (amount, currency = 'AED') => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-AE', { style: 'currency', currency }).format(amount);
};

// Sort function for table columns
const getComparator = (order, orderBy) => {
  return order === 'desc' 
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
};

// Map status codes to display values
const mapStatementStatus = (status) => {
  const statusMap = {
    'PAID': 'Paid',
    'PENDING': 'Pending',
    'OVERDUE': 'Overdue',
    'REJECTED': 'Rejected',
    'CANCELLED': 'Cancelled',
    'PARTIAL': 'Partially Paid'
  };
  return statusMap[status] || status;
};
console.log("localStorage",localStorage);
// Session validity check
const checkSessionValidity = () => {
  console.log('Checking session validity...');
  // Check if we have a valid token
  const token = getEffectiveToken(true);
  console.log('Token:21', token ? token.substring(0, 10) + '...' : null);

  if (!token) return false;
  
  // Basic check if token looks valid (not empty and has reasonable length)
  if (token.length < 20) return false;
  
  try {
    // If token is JWT format, check if it's expired
    const tokenParts = token.split('.');
    if (tokenParts.length === 3) {
      const payload = JSON.parse(atob(tokenParts[1]));
      if (payload.exp && Date.now() >= payload.exp * 1000) {
        console.log('Token has expired');
        return false;
      }
    }
  } catch (e) {
    console.warn('Error checking token expiration:', e);
  }
  
  return true;
};

const AccountStatements = () => {
  const dispatch = useDispatch();
  // Account data state
  const [accountData, setAccountData] = useState({
    accountNumber: '',
    organizationName: '',
    walletBalance: 0,
    totalInvoiced: 0,
    totalPaid: 0,
    creditLimit: 0,
    paymentTerms: '',
    accountStatus: ''
  });

  // Transaction state
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Server-side pagination total — populated from the paginated /transactions
  // and /employeeTransactions endpoints (totalElements). Falls back to local
  // length if the API returned the legacy unpaginated array.
  const [serverTotalElements, setServerTotalElements] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  // When the header "select all" is the trigger, the statement PDF is generated
  // from the current filters (all matching rows, capped server-side at 200)
  // instead of the explicitly-collected ids. Reset on any manual row toggle.
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [openCreateStatementDialog, setOpenCreateStatementDialog] = useState(false);
  const [statementName, setStatementName] = useState('');
  const [exportFormat, setExportFormat] = useState('PDF');
  const [filterStatus, setFilterStatus] = useState('');
  // Initialize startDate with Today's date as default
  const defaultToday = new Date();
  const [startDate, setStartDate] = useState(defaultToday);
  const [endDate, setEndDate] = useState(null);

  // Filter states
  const [showFilters, setShowFilters] = useState(true);
  // Initialize with Today as default filter criteria
  const [dateRange, setDateRange] = useState({ preset: 'today', startDate: defaultToday, endDate: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState('ALL'); // Default: show all assignments
  const [ticketType, setTicketType] = useState('ALL'); // Ticket type filter (WORKFLOW, STANDALONE, ALL)
  const [createdByOrgId, setCreatedByOrgId] = useState(null); // Organization that created the ticket
  const [processedByOrgId, setProcessedByOrgId] = useState(null); // Organization that processed the ticket
  const [organizations, setOrganizations] = useState([]);
  const [adminPartnerOrganizations, setAdminPartnerOrganizations] = useState([]);
  const [customerAdminOrganizations, setCustomerAdminOrganizations] = useState([]); // State for organizations fetched from API
  const [loadingOrganizations, setLoadingOrganizations] = useState(false); // Loading state for organizations
  const [isThakliAdmin, setIsThakliAdmin] = useState(false); // State to track if user is Thaklis admin

  // Partner-branding dialog state. Only relevant when a root admin has filtered
  // by a partner org — clicking download then prompts whether to brand the PDF
  // under the partner's name instead of the default Thaklis header.
  const [partnerBrandingDialog, setPartnerBrandingDialog] = useState({
    open: false,
    ticketIds: null,
    filters: null,
  });

  // Check if user is Thaklis admin
  const checkIfThakliAdmin = useCallback(() => {
    try {
      // Get authentication token via shared utility (with debug logging enabled)
      const token = getEffectiveToken(true);
      console.log('Token:23', token ? token.substring(0, 10) + '...' : null);
      if (!token) {
        console.error('No authentication token found for checking user role');
        return false;
      }
      
      // Get principal info from localStorage - this is the proper way to check user roles      
      // Try to get principalInfo from authInfo which contains role information
      const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
      const principalInfo = authInfo.principalInfo || {};
      console.log('Principal info for admin check:', principalInfo);
      
      // Check for admin roles in principalInfo
      // The roles array contains strings like "Root Admin" instead of "ROLE_ROOT_ADMIN"
      const isRootAdmin = principalInfo && principalInfo.roles && 
        (principalInfo.roles.some(role => 
            role.toUpperCase().includes('ROOT ADMIN') ||              
            role.toUpperCase().includes('THAKLIS')
        ));
          
      // Check for admin in userType
      // The userType is 'ADMIN_ORG' in the provided example
      const isAdminType = principalInfo && principalInfo.userType && 
        (principalInfo.userType.toUpperCase().includes('ADMIN_ORG') || 
          principalInfo.userType.toUpperCase().includes('ROOT ADMIN') || 
          principalInfo.userType.toUpperCase().includes('THAKLIS'));
          
      // Check organization name
      // The organizationName is 'thaklis' (lowercase) in the provided example
      const isThaklis = principalInfo && principalInfo.organisationName && 
        (principalInfo.organisationName.toUpperCase() === 'THAKLIS');
        
      const adminResult = isRootAdmin || isAdminType || isThaklis;
      console.log('Admin check result:', { isRootAdmin, isAdminType, isThaklis, adminResult });
      
      setIsThakliAdmin(adminResult);
      return adminResult;
    } catch (e) {
      console.error('Error parsing principalInfo:', e);
      return false;
    }
 
  }, []);

  // Track if initial fetch has been done to prevent duplicate calls
  const initialFetchDone = React.useRef(false);
  const customerEmployeeRoleChecked = React.useRef(false);
  const adminCheckDone = React.useRef(false);
  const filterFetchDone = React.useRef(false);

  const globalLoaderCount = React.useRef(0);
  const startGlobalLoader = useCallback((text) => {
    globalLoaderCount.current += 1;
    if (globalLoaderCount.current === 1) {
      dispatch(showLoader(text));
    }
  }, [dispatch]);

  const stopGlobalLoader = useCallback(() => {
    globalLoaderCount.current = Math.max(0, globalLoaderCount.current - 1);
    if (globalLoaderCount.current === 0) {
      dispatch(dismissLoader());
    }
  }, [dispatch]);

  const isCustomerEmployeeRef = React.useRef(false);
  const userRolePromiseRef = React.useRef(null);

  const getIsCustomerEmployee = useCallback(async (headers) => {
    if (customerEmployeeRoleChecked.current) return isCustomerEmployeeRef.current;
    if (userRolePromiseRef.current) return userRolePromiseRef.current;

    userRolePromiseRef.current = (async () => {
      let isCustomerEmployee = false;
      const axios = await import('axios');

      try {
        const userResponse = await axios.default.get(`${API_BASE_URL}/api/auth/me`, { headers });

        if (userResponse.data && userResponse.data.roles) {
          const userRoles = userResponse.data.roles;

          isCustomerEmployee = [
            'ROLE_Customer Employee',
            'CUSTOMER_EMPLOYEE',
            'Role_Customer Employee',
            'Customer Employee',
            'ROLE_CUSTOMER_EMPLOYEE'
          ].some(roleName => {
            if (Array.isArray(userRoles)) {
              return userRoles.includes(roleName) ||
                     userRoles.some(r => r === roleName ||
                                         (r && typeof r === 'object' && r.name === roleName));
            }
            return false;
          });

          const currentUserStr = localStorage.getItem('currentUser');
          if (currentUserStr) {
            try {
              const currentUser = JSON.parse(currentUserStr);
              currentUser.roles = userRoles;
              localStorage.setItem('currentUser', JSON.stringify(currentUser));
            } catch (e) {
              console.error('Error updating localStorage with roles:', e);
            }
          }
        }
      } catch (userCheckError) {
        console.error('Error checking user roles from server:', userCheckError);
        const currentUserStr = localStorage.getItem('currentUser');

        if (currentUserStr) {
          try {
            const currentUser = JSON.parse(currentUserStr);
            if (currentUser && currentUser.roles) {
              isCustomerEmployee = [
                'ROLE_Customer Employee',
                'CUSTOMER_EMPLOYEE',
                'Role_Customer Employee',
                'Customer Employee',
                'ROLE_CUSTOMER_EMPLOYEE'
              ].some(roleName => {
                if (Array.isArray(currentUser.roles)) {
                  return currentUser.roles.includes(roleName) ||
                         currentUser.roles.some(r => r === roleName ||
                                                 (r && typeof r === 'object' && r.name === roleName));
                }
                return false;
              });
            }
          } catch (e) {
            console.error('Error parsing currentUser from localStorage:', e);
          }
        }
      } finally {
        customerEmployeeRoleChecked.current = true;
        isCustomerEmployeeRef.current = isCustomerEmployee;
      }

      return isCustomerEmployee;
    })();

    return userRolePromiseRef.current;
  }, []);

  // Initialize admin check on component mount
  useEffect(() => {
    if (!adminCheckDone.current) {
      adminCheckDone.current = true;
      const isAdmin = checkIfThakliAdmin();
      // For customer users (non-admin), default to 30-day date range instead of today
      if (!isAdmin) {
        const now = new Date();
        const start = new Date();
        start.setDate(now.getDate() - 30);
        const end = new Date();
        end.setDate(now.getDate() + 1);
        setStartDate(start);
        setEndDate(end);
        setDateRange({ preset: 'last30days', startDate: start, endDate: end });
      }
    }
  }, []);

  // Fetch organizations from API
  const fetchOrganizations = useCallback(async () => {
    // The "Created By" and "Processed By" dropdowns these power are only
    // rendered for Thaklis admin users (isThakliAdmin gate in the JSX). Skip
    // both API calls for customer/partner users — they were the only fetches
    // hitting /api/organisations from this page.
    if (!checkIfThakliAdmin()) {
      return;
    }
    setLoadingOrganizations(true);
    try {
      const token = getEffectiveToken(true);
      if (!token) {
        console.error('No authentication token found for fetching organizations');
        setLoadingOrganizations(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const axios = await import('axios');

      // CUSTOMER orgs → "Created By" dropdown.
      let customerOrgs = [];
      try {
        const customerResponse = await axios.default.get(`${API_BASE_URL}/api/organisations?type=CUSTOMER`, { headers });
        if (customerResponse && customerResponse.status === 200) {
          customerOrgs = customerResponse.data.map(org => ({
            id: org.id || org.organizationId,
            name: org.name || org.organizationName,
            code: org.code || org.organizationCode,
            type: 'CUSTOMER'
          }));
        }
      } catch (customerError) {
        console.error('Error fetching customer organizations:', customerError.message);
      }

      // PARTNER orgs → "Processed By" dropdown. The previous
      // ?type=ADMIN call was dropped — Thaklis is not a valid value for
      // tticket.customer_organisation_id or tticket.partner_organisation_id,
      // so it had no effect when selected as a filter.
      let partnerOrgs = [];
      try {
        const partnerResponse = await axios.default.get(`${API_BASE_URL}/api/organisations?type=PARTNER`, { headers });
        if (partnerResponse && partnerResponse.status === 200) {
          partnerOrgs = partnerResponse.data.map(org => ({
            id: org.id || org.organizationId,
            name: org.name || org.organizationName,
            code: org.code || org.organizationCode,
            type: 'PARTNER'
          }));
        }
      } catch (partnerError) {
        console.error('Error fetching partner organizations:', partnerError.message);
      }

      const allOrgs = [...customerOrgs, ...partnerOrgs];
      setOrganizations(allOrgs);
      setAdminPartnerOrganizations(partnerOrgs);
      setCustomerAdminOrganizations(customerOrgs);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
    } finally {
      setLoadingOrganizations(false);
    }
  }, [checkIfThakliAdmin]);
  
  // Define fetchTransactions first before it's used in useEffect
  const fetchTransactions = useCallback(async () => {
    console.log('fetchTransactions called with current ticketType:', ticketType);
    setLoading(true);
    setError(null);
    startGlobalLoader('Loading...');
    
    // Check if we have a valid session before making the API call
    const isSessionValid = checkSessionValidity();
    
    if (!isSessionValid) {
      console.error('Session is not valid or has expired');
      setError(
        <div>
          <p>Your session has expired. Please log in again.</p>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.location.href = '/login'}
            style={{ marginTop: '10px' }}
          >
            Go to Login
          </Button>
        </div>
      );
      stopGlobalLoader();
      setLoading(false);
      return;
    }
    
    try {
      // Get authentication token via utility
      const token = getEffectiveToken(true);
                 
      if (!token) {
        console.error('Authentication token not found in any standard location');
        setError(
          <div>
            <p>Authentication required. Please login again.</p>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => window.location.href = '/login'}
              style={{ marginTop: '10px' }}
            >
              Go to Login
            </Button>
          </div>
        );
        stopGlobalLoader();
        setLoading(false);
        return;
      }
      
      // Log token information for debugging (safely)
      console.log('Using authentication token:', token.substring(0, 15) + '...');
      
      // Ensure token has proper format - Bearer prefix is required for API calls
      const rawToken = token.startsWith('Bearer ') ? token.substring(7) : token;
      
      const headers = {
        'Authorization': `Bearer ${rawToken}`,
        'Content-Type': 'application/json'
      };
      
      // Log authorization header (safely)
      console.log('Authorization header format:', `Bearer ${rawToken.substring(0, 15)}...`);
      
      // Get organization ID for the customer organization admin
      let organizationId;
      
      try {
        // First try the URL parameter
        const urlParams = new URLSearchParams(window.location.search);
        organizationId = urlParams.get('orgId');
        
        // Then try various localStorage options with different case formats
        if (!organizationId) {
          const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
          const authInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
          const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
          
          // Check for organizationId with different case formats (camelCase, lowercase, snake_case)
          organizationId = userInfo.organizationId || userInfo.organisationId || userInfo.organisation_id ||
                         authInfo.organizationId || authInfo.organisationId || authInfo.organisation_id ||
                         currentUser.organizationId || currentUser.organisationId || currentUser.organisation_id ||
                         localStorage.getItem('organizationId') || localStorage.getItem('organisationId') ||
                         localStorage.getItem('organisation_id');
          
          if (organizationId) {
            console.log('Found organization ID:', organizationId);
          }
        }
        
        // No need to explicitly check for organizationId
        // The /api/accounts/transactions endpoint uses the organization from the authenticated token
        console.log('Using authenticated user\'s organization from JWT token');
        // Skip organization validation check
      } catch (error) {
        console.error('Error retrieving organization ID:', error);
        setError('Unable to determine your organization. Please log out and log in again.');
        stopGlobalLoader();
        setLoading(false);
        return;
      }
      
      const queryParams = [];
      
      // Add date range parameters
      console.log('startDate:', startDate);
      console.log('endDate:', endDate);

      // Format dates as YYYY-MM-DD for API
      const formatDate = (date) => {
        if (!(date instanceof Date)) return '';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };

      if (startDate) {
        console.log('formatDate(startDate):', formatDate(startDate));
        queryParams.push(`startDate=${formatDate(startDate)}`);
      }
      if (endDate) {
        console.log('formatDate(endDate):', formatDate(endDate));
        queryParams.push(`endDate=${formatDate(endDate)}`);
      }
      
      // Add status filter if specified
      if (filterStatus && filterStatus.trim() !== 'ALL') {
        queryParams.push(`status=${filterStatus}`);
      }

      console.log('ticketType before API request:', ticketType);
      // Add ticket type filter if specified
      if (ticketType && ticketType !== 'ALL') {
        // Ensure we're using the current ticketType value
        const currentTicketType = ticketType;
        console.log('Using ticketType for API request:', currentTicketType);
        queryParams.push(`workflowService=${currentTicketType}`);
      }
      
      // Add organization filters if specified
      if (createdByOrgId) {
        queryParams.push(`customerOrgId=${createdByOrgId}`);
      }
      
      if (processedByOrgId) {
        queryParams.push(`partnerOrgId=${processedByOrgId}`);
      }

      // Server-side pagination — pushes the current page + size so the
      // backend doesn't dump every ticket on every request.
      queryParams.push(`page=${page}`);
      queryParams.push(`pageSize=${rowsPerPage}`);

      const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      console.log('Query string: ', queryString);

      const axios = await import('axios');
      const isCustomerEmployee = await getIsCustomerEmployee(headers);
      
      // Use the appropriate endpoint based on user role
      // For CUSTOMER_EMPLOYEE users, use the dedicated endpoint that only shows their transactions
      const apiUrl = isCustomerEmployee
        ? `${API_BASE_URL}/api/accounts/employeeTransactions${queryString}`
        : `${API_BASE_URL}/api/accounts/transactions${queryString}`;
      
      console.log('Final user role check:', { isCustomerEmployee, apiUrl });
      console.log(`Fetching transactions from API endpoint: ${apiUrl}`);
      
      let response = null;
      let responseData = null;
      let statements = [];
      
      try {
        // Use direct axios call to ensure headers are passed correctly
        console.log(`Making API call to: ${apiUrl}`);
        console.log('Headers being sent:', { ...headers, 'Authorization': 'Bearer [REDACTED]' });
        
        response = await axios.default.get(apiUrl, { headers });
        
        // Log the API call success
        console.log(`API call successful: ${apiUrl}`);
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
        
        if (response && response.status === 200) {
          responseData = response.data;
          console.log(`Successfully fetched data from ${apiUrl}:`, responseData);
        }
      } catch (endpointError) {
        console.error(`Error calling transactions endpoint:`, endpointError);
        console.error(`Failed endpoint: ${apiUrl}`);
        console.error('Error details:', {
          status: endpointError.response?.status,
          statusText: endpointError.response?.statusText,
          data: endpointError.response?.data,
          headers: endpointError.response?.headers
        });
        
        // If we get a 403 Forbidden error, try the other endpoint as a fallback
        if (endpointError.response?.status === 403) {
          try {
            console.log('Got 403 Forbidden, trying alternative endpoint as fallback...');
            const fallbackUrl = isCustomerEmployee
              ? `${API_BASE_URL}/api/accounts/myTransactions${queryString}`
              : `${API_BASE_URL}/api/accounts/employeeTransactions${queryString}`;
              
            console.log(`Trying fallback URL: ${fallbackUrl}`);
            const fallbackResponse = await axios.default.get(fallbackUrl, { headers });
            
            if (fallbackResponse && fallbackResponse.status === 200) {
              responseData = fallbackResponse.data;
              console.log(`Successfully fetched data from fallback ${fallbackUrl}:`, responseData);
            }
          } catch (fallbackError) {
            console.error(`Fallback endpoint also failed:`, fallbackError);
          }
        }
      }
      
      // Handle error cases
      if (!response || response.status !== 200) {
        if (response && response.status === 401) {
          console.error('Authentication failed (401)');
          setError('Your session has expired. Please log in again.');
        } else if (response && response.status === 403) {
          console.error('Authorization failed (403)');
          setError('You do not have permission to view statements for this organization.');
        } else {
          const errorMsg = `Failed to fetch statements. Status: ${response ? response.status : 'Unknown'}`;
          console.error(errorMsg);
          setError(errorMsg);
        }
        setLoading(false);
        return;
      }
      
      // The /api/accounts/transactions endpoint returns an array directly
      if (Array.isArray(responseData)) {
        statements = responseData;
        setServerTotalElements(null); // legacy unpaginated path
        console.log('Processing array response from /api/accounts/transactions:', statements.length, 'records');
        console.log("statements",statements);
      } else if (responseData && typeof responseData === 'object') {
        // Paginated response shape from the backend — items + totalElements.
        // Capture the server total so TablePagination shows the true count
        // (not just the current page's length).
        if (typeof responseData.totalElements === 'number') {
          setServerTotalElements(responseData.totalElements);
        } else {
          setServerTotalElements(null);
        }

        // Handle potential wrapper objects — `items` first (paginated), then
        // legacy fallback keys for safety.
        const possibleArrays = ['items', 'data', 'transactions', 'statements', 'content'];
        let foundArray = false;
        
        for (const key of possibleArrays) {
          if (responseData[key] && Array.isArray(responseData[key])) {
            statements = responseData[key];
            console.log(`Found data in responseData.${key}:`, statements.length, 'records');
            foundArray = true;
            break;
          }
        }
        
        if (!foundArray) {
          console.error('Unexpected response format:', responseData);
          setTransactions([]);
          setError('Received data in unexpected format. Please contact administrator.');
          setLoading(false);
          return;
        }
      } else {
        console.error('Unexpected response format:', responseData);
        setTransactions([]);
        setError('Received data in unexpected format. Please contact administrator.');
        setLoading(false);
        return;
      }
      
      // Process parent-child relationships for workflow tickets
      const parentTickets = [];
      const childTicketMap = new Map();
      
      // First pass: identify parent and child tickets
      statements.forEach(statement => {
        if (statement.hasChildTickets && statement.childTickets && statement.childTickets.length > 0) {
          // This is a parent ticket with children
          parentTickets.push(statement);
          
          // Map child ticket IDs to their parent
          statement.childTickets.forEach(childTicket => {
            childTicketMap.set(childTicket.id, statement.id);
          });
        }
      });
            
      // Filter out child tickets from the main list if they have a parent
      //statements = statements.filter(statement => !childTicketMap.has(statement.id));
      //sundari need to check
      console.log('Extracted parentTickets:', parentTickets);
      console.log('Extracted childTicketMap:', childTicketMap);
      //console.log('Extracted statements:', statements);
      
      if (!statements || statements.length === 0) {
        setTransactions([]);
        setError('No statements found for this organization.');
        setLoading(false);
        return;
      }
      
      // Format statements for display - handle the /api/accounts/transactions response format
      const formattedStatements = statements.map(statement => {
        // Handle transaction ID
        const id = statement.id || statement.transactionId || `tx-${Math.random().toString(36).substr(2, 9)}`;
        const number = statement.ticketNumber || statement.orgTicketNumber || statement.id || '';
        console.log('Extracted statement:sun1', statement);
        // Process transaction date
        let statementDate = null;
        try {
          statementDate = statement.transactionDate || statement.date || statement.createdAt || new Date();
          if (typeof statementDate === 'string') {
            statementDate = new Date(statementDate);
          }
        } catch (e) {
          console.warn('Error parsing transaction date:', e);
          statementDate = new Date();
        }
        
        // Due dates may not exist in transaction records
        let dueDate = null;
        
        // Get amount with fallbacks - in the accounts/transactions endpoint it's called totalCost
        const amount = statement.totalCost || statement.amount || statement.totalAmount || 0;
        
        // Get status with standard formatting
        let status = statement.status || 'PENDING';
        status = status.toUpperCase();
        console.log("status statement assigneduserId",statement.assigneduserId);
        console.log("status statement assigneduserRole",statement.assigneduserRole);
        console.log("status statement assignedOrganisationName",statement.assignedOrganisationName);
        console.log("status statement assignedTo",statement.assignedTo);
        console.log("status statement assignedAt",statement.assignedAt);

        return {
          id,
          statementNumber: number,
          ticketId: statement.ticketId || id,
          ticketNumber: statement.ticketNumber || number,
          transactionNumber: statement.transactionNumber || statement.transactionId || statement.transactionNo || statement.referenceNumber || '',
          date: statementDate,
          dueDate: statement.dueDate ? new Date(statement.dueDate) : null,
          amount: statement.totalAmount || statement.amount || 0,
          totalCost: statement.totalCost || statement.amount || statement.totalAmount || 0,
          status,
          description: statement.description || 'No description available',
          workflow: statement.workflowName || 'General',
          serviceName: statement.serviceName || 'General Service',
          organizationName: statement.organizationName || '',
          organizationCode: statement.organizationCode || '',
          userName: statement.userName || 'System',
          paidAmount: statement.paidAmount || 0,
          remainingAmount: statement.remainingAmount || amount,
          paymentMethod: statement.paymentMethod || '-',
          paymentDate: statement.paymentDate ? new Date(statement.paymentDate) : null,
          currency: statement.currency || 'AED',
          orgTicketNumber: statement.orgTicketNumber || '',
          serviceCharge: statement.serviceCharge || 0,
          tax: statement.tax || 0,
          govtCharge: statement.govtCharge || 0,
          fine: statement.fine || 0,
          totalCost: statement.totalCost ||  0,
          paymentStatus: statement.paymentStatus || 'PENDING',
          assignedTo: statement.assignedTo || 'Not assigned',
          assignedAt: statement.assignedAt ? new Date(statement.assignedAt) : null,
          assigneduserId: statement.assigneduserId || 'Not assigned',
          assigneduserRole: statement.assigneduserRole || 'Not assigned',
          assignedOrganisationName: statement.assignedOrganisationName || 'Not assigned',
          // Add workflow-related fields
          ticketType: statement.ticketType || (statement.workflowName && statement.workflowName !== 'General' ? 'WORKFLOW' : 'STANDALONE'),
          hasChildTickets: statement.hasChildTickets || false,
          childTickets: statement.childTickets || [],
          invoiceNumber: statement.invoiceNumber || '',
          processedDate: statement.processedDate ? new Date(statement.processedDate) : null,
          createdByOrganizationId: statement.createdByOrganizationId || null,
          createdByOrganizationName: statement.createdByOrganizationName || '',
          processedByOrganizationId: statement.processedByOrganizationId || null,
          processedByOrganizationName: statement.processedByOrganizationName || '',
          customerName: statement.customerName || '',
          customerAddress: statement.customerAddress || '',
          customerEmail: statement.customerEmail || '',
          customerPhone: statement.customerPhone || '',
        };
      });
      console.log('Formatted statements:', formattedStatements);
      
      setTransactions(formattedStatements);
    } catch (error) {
      console.error('Error fetching statements:', error);
      
      // Handle specific error cases with user-friendly messages
      if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          setError(
            <div>
              <p>Authentication failed. Please log in again.</p>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => window.location.href = '/login'}
                style={{ marginTop: '10px' }}
              >
                Go to Login
              </Button>
            </div>
          );
          // Clear invalid tokens to force re-login
          localStorage.removeItem('tkToken');
          localStorage.removeItem('authToken');
          localStorage.removeItem('auth_token');
        } else if (error.response.status === 404) {
          setError('No account statements found for your organization.');
        } else {
          setError(`Error loading statements: ${error.response.data?.message || error.message}`);
        }
      } else if (error.message?.includes('expired')) {
        setError(
          <div>
            <p>Your session has expired. Please log in again.</p>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => window.location.href = '/login'}
              style={{ marginTop: '10px' }}
            >
              Go to Login
            </Button>
          </div>
        );
        // Clear invalid tokens to force re-login
        localStorage.removeItem('tkToken');
        localStorage.removeItem('authToken');
        localStorage.removeItem('auth_token');
      } else {
        setError(
          <div>
            <p>Error loading statements: {error.message}</p>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={fetchTransactions}
              style={{ marginTop: '10px' }}
            >
              Retry
            </Button>
          </div>
        );
      }
    } finally {
      stopGlobalLoader();
      setLoading(false);
    }
  }, [selectedAccount, startDate, endDate, filterStatus, selectedAssignment, ticketType, createdByOrgId, processedByOrgId, page, rowsPerPage, startGlobalLoader, stopGlobalLoader, getIsCustomerEmployee]);

  // Initialize by loading transactions when component mounts - only once
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchTransactions();
    }
  }, []);

  // Handle request sort for table headers
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  // Define the fetchStatements function outside of useEffect to avoid dependency cycle
  const fetchStatements = useCallback(async () => {
    if (!selectedAccount) return;
    
    setLoading(true);
    try {
      let accountId = selectedAccount;

      // Get authentication token
      const token = localStorage.getItem('authToken');
      try {
        const authInfoRaw = localStorage.getItem('authInfo');
        const authInfo = authInfoRaw ? JSON.parse(authInfoRaw) : null;
        console.log('authInfo.token:', authInfo && authInfo.token ? (authInfo.token.substring(0, 10) + '...') : null);
      } catch (e) {
        console.warn('Could not parse authInfo from localStorage:', e);
      }
      console.log('Token:25', token ? token.substring(0, 10) + '...' : null);             

      if (!token) {
        console.error('No authentication token found');
        setError('Authentication failed. Please login again.');
        setLoading(false);
        return;
      }

      // Use the authenticated API endpoint that matches the account transactions pattern
      let url = `${API_BASE_URL}/api/statements/account/${accountId}`;
      const queryParams = [];

      // Add date filters if set
      if (startDate instanceof Date) {
        queryParams.push(`startDate=${startDate.toISOString().split('T')[0]}`);
      }
      
      if (endDate instanceof Date) {
        queryParams.push(`endDate=${endDate.toISOString().split('T')[0]}`);
      }

      if (queryParams.length > 0) {
        url = `${url}?${queryParams.join('&')}`;
      }
      console.log('URL: ', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        // Unauthorized, token might be expired
        console.error('Authentication token expired or invalid');
        setError('Authentication failed. Please login again.');
        localStorage.removeItem('authToken'); // Clear invalid token
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (Array.isArray(data) && data.length > 0) {
        console.log('Fetched statements:', data);
                
        // Transform backend data to frontend format
        const formattedData = data.map(statement => ({
          id: statement.id || `stmt-${Math.random().toString(36).substring(2, 9)}`,
          statementNumber: statement.statementId || statement.statementNumber || '-',
          date: statement.statementDate ? new Date(statement.statementDate) : new Date(),
          dueDate: statement.dueDate ? new Date(statement.dueDate) : null,
          amount: statement.totalAmount || statement.amount || 0,
          status: mapStatementStatus(statement.paymentStatus),
          description: statement.description || 'Statement for services',
          serviceName: statement.serviceName || 'General Service',
          paidAmount: statement.paidAmount || 0,
          remainingAmount: statement.remainingAmount || statement.totalAmount || 0,
          paymentMethod: statement.paymentMethod || '-',
          paymentDate: statement.paymentDate ? new Date(statement.paymentDate) : null,
          lineItems: statement.lineItems || [],
          currency: statement.currency || 'AED',
          orgTicketNumber: statement.orgTicketNumber || '',
          serviceCharge: statement.serviceCharge || 0,
          tax: statement.tax || 0,
          govtCharge: statement.govtCharge || 0,
          fine: statement.fine || 0,
          totalCost: statement.totalCost || 0,
          paymentStatus: statement.paymentStatus || 'PENDING',
          assignedTo: statement.assignedTo || 'Not assigned',
          assignedAt: statement.assignedAt ? new Date(statement.assignedAt) : null,
          assigneduserId: statement.assigneduserId || 'Not assigned',
          assigneduserRole: statement.assigneduserRole || 'Not assigned',
          assignedOrganisationName: statement.assignedOrganisationName || 'Not assigned',
        }));

        // Apply status filter client-side (if not 'ALL')
        let filteredData = formattedData;
        if (filterStatus && filterStatus.trim() !== 'ALL') {
          filteredData = formattedData.filter(item => 
            item.status && item.status.toUpperCase() === filterStatus.toUpperCase()
          );
        }
        
        setTransactions(filteredData);
      } else {
        console.log('No statements found or invalid data format');
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching statements:', error);
      
      // Handle specific error cases with user-friendly messages
      if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          setError(
            <div>
              <p>Authentication failed. Please log in again.</p>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => window.location.href = '/login'}
                style={{ marginTop: '10px' }}
              >
                Go to Login
              </Button>
            </div>
          );
          // Clear invalid tokens to force re-login
          localStorage.removeItem('tkToken');
          localStorage.removeItem('authToken');
          localStorage.removeItem('auth_token');
        } else if (error.response.status === 404) {
          setError('No account statements found for your organization.');
        } else {
          setError(`Error loading statements: ${error.response.data?.message || error.message}`);
        }
      } else if (error.message?.includes('expired')) {
        setError(
          <div>
            <p>Your session has expired. Please log in again.</p>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => window.location.href = '/login'}
              style={{ marginTop: '10px' }}
            >
              Go to Login
            </Button>
          </div>
        );
        // Clear invalid tokens to force re-login
        localStorage.removeItem('tkToken');
        localStorage.removeItem('authToken');
        localStorage.removeItem('auth_token');
      } else {
        setError(
          <div>
            <p>Error loading statements: {error.message}</p>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={fetchTransactions}
              style={{ marginTop: '10px' }}
            >
              Retry
            </Button>
          </div>
        );
      }
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, startDate, endDate, filterStatus, selectedAssignment, ticketType, createdByOrgId, processedByOrgId]);
  
  // Trigger fetchTransactions when any filter changes
  // Single useEffect for filter changes - debounced to prevent duplicate calls
  const filterTimeoutRef = React.useRef(null);
  useEffect(() => {
    // Skip initial render - handled by initialFetchDone
    if (!initialFetchDone.current) return;

    if (!filterFetchDone.current) {
      filterFetchDone.current = true;
      return;
    }
    
    // Clear any pending timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }
    
    // Debounce filter changes to prevent duplicate calls. Reset to page 0
    // when filters change (but not when only page/rowsPerPage changes) so
    // the user isn't stuck on a page that no longer exists after narrowing.
    filterTimeoutRef.current = setTimeout(() => {
      console.log('Filter change detected, fetching transactions');
      if (checkSessionValidity()) {
        if (page !== 0) {
          setPage(0); // setPage will trigger the effect again with page=0
        } else {
          fetchTransactions();
        }
      }
    }, 300);
    
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [startDate, endDate, filterStatus, selectedAssignment, ticketType, createdByOrgId, processedByOrgId, page, rowsPerPage]);
  
  // Fetch organizations when component mounts - only once
  const orgFetchDone = React.useRef(false);
  useEffect(() => {
    if (!orgFetchDone.current) {
      orgFetchDone.current = true;
      fetchOrganizations();
    }
  }, []);

  // Apply sorting to filtered transactions
  const filteredTransactions = useMemo(() => {
    // First apply any filters
    let filteredResults = transactions || [];
    
    // Apply assignment filter if selectedAssignment is not 'ALL'
    if (selectedAssignment && selectedAssignment !== 'ALL' && !processedByOrgId) {
      filteredResults = filteredResults.filter(transaction => 
        transaction && transaction.processedByOrganizationId === selectedAssignment
      );
    }

    // Apply ticket type filter based on workflow/standalone selection
    if (ticketType && ticketType !== 'ALL') {
      filteredResults = filteredResults.filter(transaction => {
        const workflowName = transaction.workflow || transaction.workflowName || transaction.serviceName;
        const ticketTypeValue = transaction.ticketType || (workflowName && (workflowName !== 'General' && workflowName !== 'General Service') ? 'WORKFLOW' : 'STANDALONE');
        if (ticketType === 'WORKFLOW') {
          return ticketTypeValue === 'WORKFLOW' || (workflowName && (workflowName !== 'General' && workflowName !== 'General Service'));
        } else if (ticketType === 'STANDALONE') {
          return ticketTypeValue === 'STANDALONE' || (workflowName && (workflowName === 'General' || workflowName === 'General Service'));
        }
        return true;
      });
    }
    
    // Apply date range filter if selected
    if (startDate && endDate) {
      filteredResults = filteredResults.filter(transaction => {
        const transactionDate = transaction.date;
        return transactionDate && (transactionDate >= startDate && transactionDate <= endDate);
      });
    }
    
    // Apply search filter by orgTicketNumber
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filteredResults = filteredResults.filter(transaction => {
        const ticketNumber = (transaction.orgTicketNumber || transaction.ticketNumber || '').toLowerCase();
        return ticketNumber.includes(query);
      });
    }
    
    // Sort the filtered results using the imported getComparator function
    return [...filteredResults].sort(getComparator(order, orderBy));
  }, [transactions, filterStatus, startDate, endDate, order, orderBy, selectedAssignment, ticketType, searchQuery]);

  // Display error message if any
  const renderError = () => {
    if (!error) return null;
    return (
      <div className="error-message">
        <Alert severity="error">
          {typeof error === 'object' ? (
            // If error is a React element (for login/retry buttons)
            error
          ) : (
            // If error is a string
            error
          )}
        </Alert>
      </div>
    );
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Fetch detailed transaction information from API when a transaction is selected
  const fetchTransactionDetails = useCallback(async (transactionId) => {
    
    if (!transactionId) return;
    
    setLoading(true);
    try {
      // Get auth token with fallbacks
      const token = localStorage.getItem('authToken') || 
                   localStorage.getItem('auth_token') || 
                   localStorage.getItem('jwt_token') || 
                   localStorage.getItem('token');
                   console.log('localStorage27:', localStorage.getItem('authInfo').get('token'));                               
                   console.log('Token:27', token);              
             
      if (!token) {
        console.error('No authentication token found');
        setError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      console.log('Fetching transaction details for ID:', transactionId);      
 

      // Call the API to get transaction details
      const response = await AppService.get(
        `/api/accounts/transactions/${transactionId}`, 
        { headers }
      );      

      if (response && response.status === 200) {
        console.log('Fetched transaction details: response ', response.data);
        
        // Format transaction data similar to our list formatter
        const transaction = response.data;
        const formattedTransaction = {
          id: transaction.id || transaction.transactionId,
          ticketId: transaction.ticketId,
          ticketNumber: transaction.ticketNumber || transaction.orgTicketNumber,
          date: transaction.transactionDate ? new Date(transaction.transactionDate) : new Date(),
          amount: transaction.totalCost || transaction.amount || transaction.totalAmount || 0,
          totalCost: transaction.totalCost || transaction.amount || transaction.totalAmount || 0,
          status: (transaction.status || 'PENDING').toUpperCase(),
          description: transaction.description || 'No description available',
          serviceName: transaction.serviceName,
          workflow: transaction.workflowName || 'General',
          organizationName: transaction.organizationName || '',
          organizationCode: transaction.organizationCode || '',
          userName: transaction.userName || 'System',
          currency: transaction.currency || 'AED',
          orgTicketNumber: transaction.orgTicketNumber || '',
          serviceCharge: transaction.serviceCharge || 0,
          tax: transaction.tax || 0,
          govtCharge: transaction.govtCharge || 0,
          fine: transaction.fine || 0,
          totalCost: transaction.totalCost || 0,
          paymentStatus: transaction.paymentStatus || 'PENDING',
          assignedTo: transaction.assignedTo || 'Not assigned',
          assignedAt: transaction.assignedAt ? new Date(transaction.assignedAt) : null,
          assigneduserId: transaction.assigneduserId || 'Not assigned',
          assigneduserRole: transaction.assigneduserRole || 'Not assigned',
          assignedOrganisationName: transaction.assignedOrganisationName || 'Not assigned',
          customerName: transaction.customerName || 'N/A',
        };
        console.log("formattedTransaction",formattedTransaction);
        setSelectedTransaction(formattedTransaction);
      } else {
        console.error('Failed to fetch transaction details, status:', response?.status);
        // We'll still show the transaction with the data we have
      }
    } catch (error) {
      console.error('Error fetching transaction details:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // State for expanded rows (workflow tickets with children)
  const [expandedRows, setExpandedRows] = useState([]);
  
  // Handle expand/collapse row for workflow tickets with children
  const handleExpandRow = (transaction) => {
    if (!transaction.hasChildTickets) return;
    
    setExpandedRows(prev => {
      if (prev.includes(transaction.id)) {
        return prev.filter(id => id !== transaction.id);
      } else {
        return [...prev, transaction.id];
      }
    });
  };
  
  // Handle view statement
  const handleViewStatement = (transaction) => {
    // Set the transaction from our list first for immediate display
    console.log('Fetching fresh transaction details from API for ID :', transaction);
    setSelectedTransaction(transaction);
    setViewDialogOpen(true);
    
    // Then fetch fresh details from the API
    console.log('Fetching fresh transaction details from API for ID:', transaction.id);
    fetchTransactionDetails(transaction.id);
  };
  
  // Handle download invoice - calls backend API to generate PDF
  const handleDownloadInvoice = async (transaction) => {
    if (!transaction) {
      console.error('No transaction data available');
      alert('No transaction data available for download');
      return;
    }
    
    // Collect ticket IDs - include child tickets if parent is selected
    const ticketIds = [];
    ticketIds.push(transaction.id);
    
    // If this is a parent ticket with children, include all child ticket IDs
    if (transaction.hasChildTickets && transaction.childTickets && transaction.childTickets.length > 0) {
      transaction.childTickets.forEach(child => {
        if (child.id) {
          ticketIds.push(child.id);
        }
      });
    }
    
    requestPdfDownload(ticketIds);
  };

  // Entry point for "download this transaction's PDF". When a root admin has
  // a partner-org filter active we first ask whether the PDF should be branded
  // under that partner; otherwise download immediately with the default
  // Thaklis header. Non-root users always go straight to download.
  const requestPdfDownload = (ticketIds) => {
    if (!ticketIds || ticketIds.length === 0) {
      alert('No tickets selected for download');
      return;
    }
    if (isThakliAdmin && processedByOrgId) {
      setPartnerBrandingDialog({ open: true, ticketIds, filters: null });
    } else {
      downloadPdfFromBackend(ticketIds, null);
    }
  };

  // Entry point for the "select all" PDF — generate from the current filters
  // instead of explicit ids. Same partner-branding prompt as the id flow.
  const requestPdfByFilters = (filters) => {
    if (isThakliAdmin && processedByOrgId) {
      setPartnerBrandingDialog({ open: true, ticketIds: null, filters });
    } else {
      downloadPdfByFilters(filters, null);
    }
  };

  // Shared POST + blob-download for the statement PDF endpoint. `body` is either
  // { ticketIds, partnerOrganisationId? } (explicit selection) or
  // { filters, partnerOrganisationId? } (select-all matching).
  const postStatementPdf = async (body) => {
    setLoading(true);
    try {
      const token = getEffectiveToken(true);
      if (!token) {
        setError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }

      const axios = await import('axios');
      const response = await axios.default.post(
        `${API_BASE_URL}/v1/accounts/statements/pdf`,
        body,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      // Create download link from blob response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `statement_${Date.now()}.pdf`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.+)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/"/g, '');
        }
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Download PDF for an explicit list of ticket IDs. `partnerOrganisationId` is
  // optional — when provided the backend renders the partner's name + address in
  // the header (ROOT_ADMIN only, enforced server-side).
  const downloadPdfFromBackend = (ticketIds, partnerOrganisationId) => {
    if (!ticketIds || ticketIds.length === 0) {
      alert('No tickets selected for download');
      return;
    }
    const body = { ticketIds };
    if (partnerOrganisationId) {
      body.partnerOrganisationId = partnerOrganisationId;
    }
    return postStatementPdf(body);
  };

  // Download PDF for ALL rows matching the current filters (the "select all"
  // flow). The backend runs the same filtered query as the list, capped at 200;
  // the PDF itself shows the applied filters and how many rows were left out.
  const downloadPdfByFilters = (filters, partnerOrganisationId) => {
    const body = { filters };
    if (partnerOrganisationId) {
      body.partnerOrganisationId = partnerOrganisationId;
    }
    return postStatementPdf(body);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const selectableRowIds = useMemo(() => {
    const ids = [];
    (filteredTransactions || []).forEach((t) => {
      if (t?.id) ids.push(t.id);
      if (t?.hasChildTickets && Array.isArray(t.childTickets)) {
        t.childTickets.forEach((child) => {
          if (child?.id) ids.push(child.id);
        });
      }
    });
    return ids;
  }, [filteredTransactions]);

  // Selected rows handlers
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedRows(selectableRowIds);
      setSelectAllMatching(true);
      return;
    }
    setSelectedRows([]);
    setSelectAllMatching(false);
  };

  const handleSelectRow = (id, isParent = false, childTickets = []) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected = [...selectedRows];

    if (selectedIndex === -1) {
      // Adding selection
      newSelected.push(id);
      // If this is a parent ticket with children, select all children too
      if (isParent && childTickets && childTickets.length > 0) {
        childTickets.forEach(child => {
          if (child.id && !newSelected.includes(child.id)) {
            newSelected.push(child.id);
          }
        });
      }
    } else {
      // Removing selection
      newSelected = newSelected.filter(rowId => rowId !== id);
      // If this is a parent ticket, also deselect all children
      if (isParent && childTickets && childTickets.length > 0) {
        const childIds = childTickets.map(c => c.id);
        newSelected = newSelected.filter(rowId => !childIds.includes(rowId));
      }
    }

    setSelectedRows(newSelected);
    // A manual toggle means this is no longer "all matching" — fall back to the
    // explicit-id PDF flow.
    setSelectAllMatching(false);
  };

  const isSelected = (id) => selectedRows.indexOf(id) !== -1;

  const isSelectAllChecked =
    selectableRowIds.length > 0 && selectedRows.length === selectableRowIds.length;

  const isSelectAllIndeterminate =
    selectedRows.length > 0 && selectedRows.length < selectableRowIds.length;

    const selectedCount = selectedRows.length;

  // Map the active date-range preset to a human label shown in the PDF header.
  const PERIOD_LABELS = {
    today: 'Last 24 hours',
    last7days: 'Last 7 days',
    last30days: 'Last 30 days',
    last90days: 'Last 90 days',
    custom: 'Custom range'
  };

  // Build the current filter set for the "select all" PDF flow. Mirrors the
  // params fetchTransactions sends, so the PDF covers exactly the rows the list
  // is showing — across all pages, not just the loaded page.
  const buildCurrentFilters = () => {
    const fmt = (d) => {
      if (!(d instanceof Date)) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    return {
      startDate: fmt(startDate),
      endDate: fmt(endDate),
      status: (filterStatus && filterStatus.trim() !== '' && filterStatus.trim() !== 'ALL') ? filterStatus.trim() : null,
      workflowService: (ticketType && ticketType !== 'ALL') ? ticketType : null,
      customerOrgId: createdByOrgId || null,
      partnerOrgId: processedByOrgId || null,
      searchQuery: (searchQuery && searchQuery.trim()) ? searchQuery.trim() : null,
      periodLabel: PERIOD_LABELS[dateRange?.preset] || null
    };
  };

  // Create statement from selected tickets - download PDF via backend API
  const handleCreateStatement = async () => {
    if (selectedRows.length === 0) return;

    // "Select all" → generate from the current filters (all matching rows,
    // capped server-side at 200), not the explicitly-collected ids.
    if (selectAllMatching) {
      requestPdfByFilters(buildCurrentFilters());
      setSelectedRows([]);
      setSelectAllMatching(false);
      return;
    }

    // Collect all ticket IDs including child tickets for selected parent tickets
    const allTicketIds = [];
    selectedRows.forEach(rowId => {
      allTicketIds.push(rowId);
      // Find the transaction and add child ticket IDs if it's a parent
      const transaction = transactions.find(t => t.id === rowId);
      if (transaction && transaction.hasChildTickets && transaction.childTickets) {
        transaction.childTickets.forEach(child => {
          if (child.id && !allTicketIds.includes(child.id)) {
            allTicketIds.push(child.id);
          }
        });
      }
    });
    
    requestPdfDownload(allTicketIds);
    setSelectedRows([]);
  };

  // Get color based on status
  const getStatusColor = (status) => {
    switch(status && status.toUpperCase()) {
      case 'PAID':
        return 'success';
      case 'PENDING':
        return 'primary';
      case 'OVERDUE':
      case 'REJECTED':
        return 'error';
      case 'CANCELED':
      case 'CANCELLED':
        return 'default';
      default:
        return 'default';
    }
  };

  // Get human-readable label for status
  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';

    const statusMap = {
      'PAID': 'Paid',
      'PENDING': 'Pending',
      'OVERDUE': 'Overdue',
      'REJECTED': 'Rejected',
      'CANCELED': 'Canceled',
      'CANCELLED': 'Canceled'
    };

    return statusMap[status.toUpperCase()] || status;
  };

  // Get payment status chip color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'PENDING':
        return 'primary';
      case 'OVERDUE':
      case 'REJECTED':
        return 'error';
      case 'CANCELED':
      case 'CANCELLED':
        return 'default';
      default:
        return 'default';
    }
  };

  // Define table columns
  const columns = [
    { 
      field: 'expand', 
      headerName: '', 
      width: 50, 
      sortable: false,
      renderCell: (params) => (
        params.row.hasChildTickets ? (
          <IconButton 
            size="small" 
            onClick={() => handleExpandRow(params.row)}
          >
            {expandedRows.includes(params.row.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        ) : null
      )
    },
    { 
      field: 'orgTicketNumber', 
      headerName: 'Ticket Number', 
      width: 150, 
      sortable: true,
      renderCell: (params) => params.row.orgTicketNumber || params.value
    },
    {
      field: 'transactionNumber',
      headerName: 'Transaction ID',
      width: 160,
      sortable: true,
      renderCell: (params) => params.row.transactionNumber || params.value || 'N/A'
    },
    { field: 'serviceName', headerName: 'Service', width: 180, sortable: true },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120, 
      renderCell: (params) => (
        <Chip 
          label={getStatusLabel(params.value)} 
          color={getStatusColor(params.value)} 
          size="small" 
        />
      )
    },
    ...(isThakliAdmin ? [
      {
        field: 'organizationName',
        headerName: 'Customer Organization',
        width: 200,
        sortable: true,
        renderCell: (params) => params.row.organizationName || 'N/A'
      }
    ] : []),
    { 
      field: 'customerName', 
      headerName: 'Customer Name', 
      width: 180, 
      sortable: true,
      renderCell: (params) => {
        const name = params.row.customerName;
        return name ? String(name).toUpperCase() : 'N/A';
      }
    },
    { 
      field: 'govtCharge', 
      headerName: 'Gov Fee', 
      width: 120, 
      sortable: true,
      renderCell: (params) => formatCurrency(params.value)
    },
    { 
      field: 'serviceCharge', 
      headerName: 'Service Fee', 
      width: 120, 
      sortable: true,
      renderCell: (params) => formatCurrency(params.value)
    },
    { 
      field: 'tax', 
      headerName: 'Tax', 
      width: 110, 
      sortable: true,
      renderCell: (params) => formatCurrency(params.value)
    },
    { 
      field: 'fine', 
      headerName: 'Fine', 
      width: 110, 
      sortable: true,
      renderCell: (params) => formatCurrency(params.value)
    },
    { 
      field: 'totalCost', 
      headerName: 'Total', 
      width: 120, 
      sortable: true,
      renderCell: (params) => formatCurrency(params.value)
    },
    { 
      field: 'paymentStatus', 
      headerName: 'Payment Status', 
      width: 130, 
      renderCell: (params) => (
        <Chip 
          label={getStatusLabel(params.value || 'PENDING')} 
          color={getPaymentStatusColor(params.value || 'PENDING')} 
          size="small" 
          variant="outlined"
        />
      )
    },
    { 
      field: 'processedDate', 
      headerName: 'Processed Date', 
      width: 130, 
      sortable: true,
      renderCell: (params) => params.value ? formatDate(params.value) : 'N/A'
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleViewStatement(params.row)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDownloadInvoice(params.row)}>
            <GetAppIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  
  // Handle date range preset selection
  const handleDateRangePreset = (preset) => {
    const now = new Date();
    let start = null;
    let end = null;
    switch (preset) {
      case 'today':
        start = new Date();
        setStartDate(start);
        setEndDate(null);
        break;
      case 'last7days':
        start = new Date();
        start.setDate(now.getDate() - 7);
        end = new Date();
        end.setDate(now.getDate() + 1);
        setStartDate(start);
        setEndDate(end);
        break;
      case 'last30days':
        start = new Date();
        start.setDate(now.getDate() - 30);
        end = new Date();
        end.setDate(now.getDate() + 1);
        setStartDate(start);
        setEndDate(end);
        break;
      case 'last90days':
        start = new Date();
        start.setDate(now.getDate() - 90);
        end = new Date();
        end.setDate(now.getDate() + 1);
        setStartDate(start);
        setEndDate(end);
        break;
      case 'custom':
        // Don't change dates for custom, just update the preset
        setDateRange(prev => ({ ...prev, preset }));
        return;
      default:
        break;
    }

    setDateRange({
      startDate: start,
      endDate: end,
      preset
    });
  };
  

  // Handle date range filter
  const handleApplyDateFilter = () => {
    fetchTransactions();
  };
  
  // Handle predefined date range selection
  const handlePredefinedDateRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    setStartDate(start);
    setEndDate(end);
    // Fetch will be triggered by the useEffect that watches startDate and endDate
  };

    // Filter Bar component - Production ready compact design
  const FilterBar = () => (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
      {/* Header Row with Title and Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>Filters</Typography>
          {/* Ticket Type Toggle Buttons */}
          <Stack direction="row" spacing={0.5}>
            <Button 
              size="small" 
              variant={ticketType === 'WORKFLOW' ? 'contained' : 'outlined'}
              onClick={() => setTicketType('WORKFLOW')}
              sx={{ 
                fontSize: '0.75rem', 
                py: 0.5, 
                px: 1.5,
                minWidth: 'auto',
                borderColor: ticketType === 'WORKFLOW' ? '#1976d2' : '#bdbdbd',
                color: ticketType === 'WORKFLOW' ? '#fff' : '#666',
                '&:hover': { borderColor: '#1976d2' }
              }}
            >
              Workflow
            </Button>
            <Button 
              size="small" 
              variant={ticketType === 'STANDALONE' ? 'contained' : 'outlined'}
              onClick={() => setTicketType('STANDALONE')}
              sx={{ 
                fontSize: '0.75rem', 
                py: 0.5, 
                px: 1.5,
                minWidth: 'auto',
                borderColor: ticketType === 'STANDALONE' ? '#1976d2' : '#bdbdbd',
                color: ticketType === 'STANDALONE' ? '#fff' : '#666',
                '&:hover': { borderColor: '#1976d2' }
              }}
            >
              Standalone
            </Button>
            <Button 
              size="small" 
              variant={ticketType === 'ALL' ? 'contained' : 'outlined'}
              onClick={() => setTicketType('ALL')}
              sx={{ 
                fontSize: '0.75rem', 
                py: 0.5, 
                px: 1.5,
                minWidth: 'auto',
                borderColor: ticketType === 'ALL' ? '#1976d2' : '#bdbdbd',
                color: ticketType === 'ALL' ? '#fff' : '#666',
                '&:hover': { borderColor: '#1976d2' }
              }}
            >
              All
            </Button>
          </Stack>
        </Box>
        <Button
          size="small"
          variant="text"
          startIcon={<FilterListIcon fontSize="small" />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{ fontSize: '0.75rem', color: '#2e7d32', textTransform: 'none' }}
        >
          {showFilters ? 'Less Filters' : 'More Filters'}
        </Button>
      </Box>

      {showFilters && (
        <Box sx={{ pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Date Range Buttons */}
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>Date Range</Typography>
                {[
                  { key: 'today', label: '24hr' },
                  { key: 'last7days', label: '7 Days' },
                  { key: 'last30days', label: '30 Days' },
                  { key: 'last90days', label: '90 Days' },
                  { key: 'custom', label: 'Custom' }
                ].map(({ key, label }) => (
                  <Button 
                    key={key}
                    size="small" 
                    variant={dateRange.preset === key ? 'contained' : 'outlined'}
                    onClick={() => handleDateRangePreset(key)}
                    sx={{
                      fontSize: '0.7rem',
                      py: 0.25,
                      px: 1,
                      minWidth: 'auto',
                      ...(dateRange.preset === key ? { 
                        backgroundColor: '#2e7d32',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#1b5e20' }
                      } : {
                        borderColor: '#2e7d32',
                        color: '#2e7d32',
                        '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.08)' }
                      })
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
            </Grid>
            
            {/* Custom Date Pickers */}
            {dateRange.preset === 'custom' && (
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1}>
                  <DatePicker
                    label="From"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-root': { fontSize: '0.8rem' } } } }}
                  />
                  <DatePicker
                    label="To"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-root': { fontSize: '0.8rem' } } } }}
                  />
                </Stack>
              </Grid>
            )}
         
          {/* Organization Filters - Only visible for Thaklis admin users */}
          {isThakliAdmin && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Organization Filters</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="created-by-org-label">Customer Organization</InputLabel>
                    <Select
                      labelId="created-by-org-label"
                      value={createdByOrgId || ''}
                      onChange={(e) => {
                        setCreatedByOrgId(e.target.value);
                      }}
                      label="Customer Organization"
                      endAdornment={
                        createdByOrgId && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCreatedByOrgId(null);
                            }}
                            sx={{ mr: 2 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )
                      }
                    >
                      {loadingOrganizations ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        customerAdminOrganizations.map(org => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="processed-by-org-label">Partner Organization</InputLabel>
                    <Select
                      labelId="processed-by-org-label"
                      value={processedByOrgId || ''}
                      onChange={(e) => {
                        const selectedOrgId = e.target.value;
                        setProcessedByOrgId(selectedOrgId);
                        
                        if (selectedOrgId) {
                          const selectedOrg = adminPartnerOrganizations.find(org => org.id === selectedOrgId);
                          if (selectedOrg) {
                            setSelectedAssignment(selectedOrgId);
                          } else {
                            setSelectedAssignment('ALL');
                          }
                        } else {
                          setSelectedAssignment('ALL');
                        }
                      }}
                      label="Partner Organization"
                      endAdornment={
                        processedByOrgId && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProcessedByOrgId(null);
                              setSelectedAssignment('ALL');
                            }}
                            sx={{ mr: 2 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )
                      }
                    >
                      {loadingOrganizations ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        adminPartnerOrganizations.map(org => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          )}
          
          </Grid>
        </Box>
      )}
    </Paper>
  );
  
  // Search input ref to maintain focus
  const searchInputRef = React.useRef(null);
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>Account Statements</Typography>
            <Typography variant="caption" color="text.secondary">
              Account {' > '} Statements
            </Typography>
          </Box>
          <Box>
            <Button 
              variant="contained" 
              onClick={() => window.history.back()}
              size="small"
              startIcon={<ArrowBackIcon fontSize="small" />}
              sx={{ 
                fontSize: '0.8rem', 
                py: 0.5, 
                px: 1.5, 
                fontWeight: 500,
                backgroundColor: '#2e7d32',
                color: '#ffffff',
                '&:hover': { backgroundColor: '#1b5e20' }
              }}
            >
              Back to Account
            </Button>
          </Box>
        </Box>
        
        
        {/* Filter Bar */}
        <FilterBar />
        
        {/* Search Field - Outside FilterBar to prevent re-render focus loss */}
        <Box sx={{ mb: 2 }}>
          <TextField
            inputRef={searchInputRef}
            fullWidth
            size="small"
            label="Search by Ticket Number"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            placeholder="Enter ticket number..."
            sx={{ maxWidth: 300 }}
          />
        </Box>
        
        {/* Statements Table */}
        <Box sx={{ width: '100%', overflow: 'hidden', mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
            <Typography variant="subtitle1" component="div" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
              Statement Transactions ({filteredTransactions.length})
            </Typography>
            <Box>
              {selectedRows.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ReceiptIcon sx={{ fontSize: '0.9rem' }} />}
                  onClick={handleCreateStatement}
                  sx={{ mr: 1, fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                  size="small"
                >
                  Create Statement ({selectedCount})
                </Button>
              )}
              {selectedCount > 0 && (
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 1 }}
                >
                  Selected: {selectedCount}
                </Typography>
              )}
            </Box>
          </Box>
          
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small" sx={{ 
                '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.5)' },
                '& .MuiTableCell-head': { backgroundColor: '#004d40', color: 'white' } 
              }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ bgcolor: '#004d40', position: 'sticky', top: 0, zIndex: 2 }}>
                    <Checkbox
                      color="primary"
                      checked={isSelectAllChecked}
                      indeterminate={isSelectAllIndeterminate}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  {columns.map(column => (
                    <TableCell 
                      key={column.field}
                      align={column.field === 'amount' ? 'right' : 'left'}
                      sx={{ fontSize: '0.8rem', color: 'white !important', backgroundColor: '#004d40' }}
                    >
                      {column.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const allRows = [];
                  
                  filteredTransactions.forEach(transaction => {
                    const isExpanded = expandedRows.includes(transaction.id);
                    
                    // Add main transaction row
                    allRows.push(
                      <TableRow key={transaction.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={selectedRows.includes(transaction.id)}
                            onChange={() => handleSelectRow(transaction.id, transaction.hasChildTickets, transaction.childTickets)}
                          />
                        </TableCell>
                        {columns.map(column => {
                          const value = transaction[column.field];
                          return (
                            <TableCell 
                              key={column.field}
                              align={column.field === 'amount' ? 'right' : 'left'}
                              sx={{ fontSize: '0.8rem' }}
                            >
                              {column.renderCell ? column.renderCell({ row: transaction, value }) : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                    
                    // If this row is expanded and has child tickets, add child rows
                    if (isExpanded && transaction.hasChildTickets && transaction.childTickets && transaction.childTickets.length > 0) {
                      transaction.childTickets.forEach((childTicket, index) => {
                        allRows.push(
                          <TableRow 
                            key={`${transaction.id}-child-${index}`}
                            sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                size="small"
                                checked={selectedRows.includes(childTicket.id)}
                                onChange={() => handleSelectRow(childTicket.id, false, [])}
                              />
                            </TableCell>
                            {columns.map(column => {
                              // Skip expand column for child rows
                              if (column.field === 'expand') {
                                return <TableCell key={column.field}></TableCell>;
                              }

                              const value = childTicket[column.field];

                              return (
                                <TableCell 
                                  key={column.field}
                                  align={column.field === 'amount' ? 'right' : 'left'}
                                  sx={{ fontSize: '0.8rem', pl: column.field === 'orgTicketNumber' ? 4 : 2 }}
                                >
                                  {column.field === 'orgTicketNumber' && (
                                    <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>
                                      <ArrowRightIcon fontSize="small" sx={{ verticalAlign: 'middle', fontSize: '0.8rem' }} />
                                    </Box>
                                  )}
                                  {column.renderCell ? column.renderCell({ row: childTicket, value }) : value}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      });
                    }
                  });
                  
                  return allRows;
                })()}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredTransactions.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No statements found for the selected filters.
              </Typography>
            </Box>
          )}
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={serverTotalElements != null ? serverTotalElements : filteredTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ 
              '.MuiTablePagination-toolbar': { minHeight: 36, padding: '0 8px' }, 
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem' }, 
              '& .MuiInputBase-root': { fontSize: '0.75rem', marginRight: '8px' }
            }}
          />
        </Box>
        
        {/* Transaction Detail Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Transaction Details</Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setViewDialogOpen(false)}
                aria-label="close"
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedTransaction && (
              <>
                {/* Header section with billed to organization */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Billed to</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedTransaction.organizationName || selectedTransaction.createdByOrganizationName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {selectedTransaction.ticketType === 'WORKFLOW' && (
                        <>
                          <Typography variant="subtitle2" color="textSecondary">Workflow ID</Typography>
                          <Typography variant="body1">
                            {selectedTransaction.orgTicketNumber || selectedTransaction.workflowOrgSequenceNumber || selectedTransaction.workflowId || 'N/A'}
                          </Typography>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Box>
                
                {/* Main content - different display based on ticket type */}
                {selectedTransaction.ticketType === 'WORKFLOW' && selectedTransaction.hasChildTickets ? (
                  // Parent workflow ticket with child tickets - show tabular form
                  <>
                    {/* Customer information only for workflow parent tickets */}
                    <Typography variant="h6" gutterBottom>Customer Information</Typography>
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="textSecondary">Customer Name</Typography>
                          <Typography variant="body1">
                            {selectedTransaction.customerName || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    
                    {/* Cost information in tabular form */}
                    <Typography variant="h6" gutterBottom>Cost Information</Typography>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell>Service Charge</TableCell>
                            <TableCell>Government Charge</TableCell>
                            <TableCell>Tax</TableCell>
                            <TableCell>Total Cost</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>{formatCurrency(selectedTransaction.serviceCharge)}</TableCell>
                            <TableCell>{formatCurrency(selectedTransaction.govtCharge)}</TableCell>
                            <TableCell>{formatCurrency(selectedTransaction.tax)}</TableCell>
                            <TableCell><strong>{formatCurrency(selectedTransaction.totalCost)}</strong></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {/* Child tickets table with combined ticket and cost information */}
                    {selectedTransaction.childTickets && selectedTransaction.childTickets.length > 0 && (
                      <>
                        <Typography variant="h6" gutterBottom>Child Tickets</Typography>
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell>Ticket ID</TableCell>
                                <TableCell>Ticket Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Service Name</TableCell>
                                <TableCell>Service Charge</TableCell>
                                <TableCell>Govt Charge</TableCell>
                                <TableCell>Tax</TableCell>
                                <TableCell>Total Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedTransaction.childTickets.map((childTicket, index) => (
                                <TableRow key={`child-${index}`}>
                                  <TableCell>{childTicket.ticketNumber || childTicket.orgTicketNumber || 'N/A'}</TableCell>
                                  <TableCell>{childTicket.transactionDate ? formatDate(childTicket.tr) : 'N/A'}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={getStatusLabel(childTicket.status)} 
                                      size="small" 
                                      color={getStatusColor(childTicket.status)}
                                    />
                                  </TableCell>
                                  <TableCell>{childTicket.serviceName || 'N/A'}</TableCell>
                                  <TableCell>{formatCurrency(childTicket.serviceCharge || 0)}</TableCell>
                                  <TableCell>{formatCurrency(childTicket.govtCharge || 0)}</TableCell>
                                  <TableCell>{formatCurrency(childTicket.tax || 0)}</TableCell>
                                  <TableCell><strong>{formatCurrency(childTicket.totalCost || 0)}</strong></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </>
                ) : (
                  // Standalone ticket or workflow ticket without children - show in free form
                  <Grid container spacing={2}>
                    {/* Basic ticket information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>Ticket Information</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">Ticket ID</Typography>
                      <Typography variant="body1" gutterBottom>{selectedTransaction.ticketNumber || selectedTransaction.orgTicketNumber}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">Ticket Date</Typography>
                      <Typography variant="body1" gutterBottom>{formatDate(selectedTransaction.transactionDate)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                      <Chip 
                        label={getStatusLabel(selectedTransaction.status)} 
                        size="small" 
                        color={getStatusColor(selectedTransaction.status)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">Processed Date</Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedTransaction.processedDate ? formatDate(selectedTransaction.processedDate) : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">Service Name</Typography>
                      <Typography variant="body1" gutterBottom>{selectedTransaction.serviceName || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="subtitle2" color="textSecondary">Customer Name</Typography>
                      <Typography variant="body1" gutterBottom>{selectedTransaction.customerName || 'N/A'}</Typography>
                    </Grid>
                    
                    {/* Cost information */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>Cost Information</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Service Charge</Typography>
                      <Typography variant="body1" gutterBottom>{formatCurrency(selectedTransaction.serviceCharge)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Government Charge</Typography>
                      <Typography variant="body1" gutterBottom>{formatCurrency(selectedTransaction.govtCharge)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Tax</Typography>
                      <Typography variant="body1" gutterBottom>{formatCurrency(selectedTransaction.tax)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="textSecondary">Total Cost</Typography>
                      <Typography variant="body1" fontWeight="bold" gutterBottom>{formatCurrency(selectedTransaction.totalCost)}</Typography>
                    </Grid>
                    
                    {/* Additional information */}
                    {selectedTransaction.description && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" gutterBottom>Additional Information</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                          <Typography variant="body1" gutterBottom>{selectedTransaction.description}</Typography>
                        </Grid>
                      </>
                    )}
                    
                    {/* Workflow specific information */}
                    {selectedTransaction.ticketType === 'WORKFLOW' && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" gutterBottom>Workflow Information</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="textSecondary">Workflow Name</Typography>
                          <Typography variant="body1" gutterBottom>{selectedTransaction.workflow || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="textSecondary">Workflow Status</Typography>
                          <Typography variant="body1" gutterBottom>{selectedTransaction.workflowStatus || selectedTransaction.status || 'N/A'}</Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              variant="outlined" 
              onClick={() => setViewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Create Statement Dialog */}
        <Dialog
          open={openCreateStatementDialog}
          onClose={() => setOpenCreateStatementDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Create Statement</Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setOpenCreateStatementDialog(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Creating statement for {selectedRows.length} {selectedRows.length === 1 ? 'transaction' : 'transactions'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Statement Name"
                  value={statementName}
                  onChange={(e) => setStatementName(e.target.value)}
                  placeholder="Enter a name for this statement"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Export Format</Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                  >
                    <FormControlLabel 
                      value="PDF" 
                      control={<Radio />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PictureAsPdfIcon color="error" sx={{ mr: 1 }} />
                          <span>PDF</span>
                        </Box>
                      } 
                    />
                    <FormControlLabel 
                      value="EXCEL" 
                      control={<Radio />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                          <span>Excel</span>
                        </Box>
                      } 
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreateStatementDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                setOpenCreateStatementDialog(false);
                handleCreateStatement();
              }} 
              variant="contained" 
              color="primary"
              disabled={!statementName.trim()}
              startIcon={<GetAppIcon />}
            >
              Generate Statement
            </Button>
          </DialogActions>
        </Dialog>

        {/* Partner-branding prompt — only fires for root admin + partner filter.
            "Yes" sends partnerOrganisationId so the backend renders that partner's
            company name + address in the PDF header; "No" keeps the default Thaklis
            header (existing behaviour). */}
        <Dialog
          open={partnerBrandingDialog.open}
          onClose={() => setPartnerBrandingDialog({ open: false, ticketIds: null, filters: null })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Generate in Partner's Name?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              You filtered by partner{" "}
              <b>
                {(adminPartnerOrganizations.find(o => o.id === processedByOrgId) || {}).name || 'Selected partner'}
              </b>
              .
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate the statement with the partner organisation's company name and address shown in the
              header? Choose <b>No</b> to keep the default Thaklis header.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                const { ticketIds, filters } = partnerBrandingDialog;
                setPartnerBrandingDialog({ open: false, ticketIds: null, filters: null });
                if (filters) {
                  downloadPdfByFilters(filters, null);
                } else {
                  downloadPdfFromBackend(ticketIds, null);
                }
              }}
            >
              No, use Thaklis
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const { ticketIds, filters } = partnerBrandingDialog;
                const partnerId = processedByOrgId;
                setPartnerBrandingDialog({ open: false, ticketIds: null, filters: null });
                if (filters) {
                  downloadPdfByFilters(filters, partnerId);
                } else {
                  downloadPdfFromBackend(ticketIds, partnerId);
                }
              }}
            >
              Yes, use partner
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AccountStatements;
